import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { NUTRITION_SERVICE_FE_CONFIG } from './nutrition-service-fe.config';
import type {
  SearchResponse,
  SearchParams,
  NutrientMatrix,
  BulkMatrixRequest,
  BulkMatrixResponse,
  HealthResponse,
  IngredientListItem,
  IngredientListResponse,
  IngredientCreateUpdate,
  MetadataListResponse,
  MetadataListItem,
  MetadataCreateUpdate,
  UnitListResponse,
  UnitListItem,
  UnitCreateUpdate,
  BlsStammdatenListResponse,
  BlsStammdatenListItem,
  StammdatenCreate,
} from '../interfaces/nutrition.dto';

type TextFilterValue = { value: string; matchMode: string };

/**
 * HTTP client for nutrient-matrix-service-py API.
 *
 * Provides:
 * - Ingredient search for autocomplete
 * - Single/bulk matrix lookup for nutrition calculation
 */
@Injectable({
  providedIn: 'root',
})
export class NutritionServiceFe {
  private readonly http = inject(HttpClient);
  private readonly config = inject(NUTRITION_SERVICE_FE_CONFIG, {
    optional: true,
  });

  private getEndpoint(): string {
    return this.config?.endpoint ?? '/nutrient-api';
  }

  /**
   * Health check endpoint.
   */
  health(): Observable<HealthResponse> {
    return this.http.get<HealthResponse>(`${this.getEndpoint()}/api/v1/health`);
  }

  /**
   * Search ingredients by name for autocomplete.
   *
   * @param params Search parameters (q required, min 2 chars)
   */
  search(params: SearchParams): Observable<SearchResponse> {
    let httpParams = new HttpParams().set('q', params.q);

    if (params.limit != null) {
      httpParams = httpParams.set('limit', params.limit);
    }
    if (params.offset != null) {
      httpParams = httpParams.set('offset', params.offset);
    }
    if (params.is_vegan != null) {
      httpParams = httpParams.set('is_vegan', params.is_vegan);
    }
    if (params.is_glutenfree != null) {
      httpParams = httpParams.set('is_glutenfree', params.is_glutenfree);
    }
    if (params.is_lactosefree != null) {
      httpParams = httpParams.set('is_lactosefree', params.is_lactosefree);
    }
    if (params.is_nutfree != null) {
      httpParams = httpParams.set('is_nutfree', params.is_nutfree);
    }
    if (params.exclude_fish != null) {
      httpParams = httpParams.set('exclude_fish', params.exclude_fish);
    }
    if (params.exclude_seafood != null) {
      httpParams = httpParams.set('exclude_seafood', params.exclude_seafood);
    }
    if (params.exclude_meat != null) {
      httpParams = httpParams.set('exclude_meat', params.exclude_meat);
    }
    if (params.exclude_dairy != null) {
      httpParams = httpParams.set('exclude_dairy', params.exclude_dairy);
    }
    if (params.exclude_egg != null) {
      httpParams = httpParams.set('exclude_egg', params.exclude_egg);
    }

    return this.http.get<SearchResponse>(
      `${this.getEndpoint()}/api/v1/search`,
      { params: httpParams },
    );
  }

  /**
   * Get nutrient matrix for a single ingredient.
   *
   * @param blsCode BLS ingredient code
   */
  getMatrix(blsCode: string): Observable<NutrientMatrix> {
    return this.http.get<NutrientMatrix>(
      `${this.getEndpoint()}/api/v1/matrix/${encodeURIComponent(blsCode)}`,
    );
  }

  /**
   * Get nutrient matrix for an exact metadata selection.
   *
   * @param metadataId Metadata primary key
   */
  getMatrixByMetadataId(metadataId: number): Observable<NutrientMatrix> {
    return this.http.get<NutrientMatrix>(
      `${this.getEndpoint()}/api/v1/matrix/by-metadata-id/${metadataId}`,
    );
  }

  /**
   * Bulk fetch nutrient matrices for multiple ingredients.
   * More efficient than multiple single requests.
   *
   * @param blsCodes Array of BLS codes (max 100)
   */
  getMatricesBulk(blsCodes: string[]): Observable<BulkMatrixResponse> {
    const request: BulkMatrixRequest = { bls_codes: blsCodes };
    return this.http.post<BulkMatrixResponse>(
      `${this.getEndpoint()}/api/v1/matrix/bulk`,
      request,
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // INGREDIENTS LIST (READ-ONLY)
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * List all BLS ingredients (read-only).
   *
   * @param limit Max results per page (default 50)
   * @param offset Pagination offset (default 0)
   */
  listIngredients(
    limit = 50,
    offset = 0,
    search?: string,
    orderBy?: string,
    orderDir?: 'asc' | 'desc',
    textFilters?: Record<string, TextFilterValue>,
  ): Observable<IngredientListResponse> {
    let params = new HttpParams().set('limit', limit).set('offset', offset);
    if (search) params = params.set('search', search);
    if (textFilters) {
      for (const [field, { value, matchMode }] of Object.entries(textFilters)) {
        params = params.set(`tf__${field}__${matchMode}`, value);
      }
    }
    if (orderBy) params = params.set('order_by', orderBy);
    if (orderDir) params = params.set('order_dir', orderDir);
    return this.http.get<IngredientListResponse>(
      `${this.getEndpoint()}/api/v1/ingredients/list`,
      { params },
    );
  }

  getIngredient(blsCode: string): Observable<IngredientListItem> {
    return this.http.get<IngredientListItem>(
      `${this.getEndpoint()}/api/v1/ingredients/${encodeURIComponent(blsCode)}`,
    );
  }

  createIngredient(
    data: IngredientCreateUpdate,
  ): Observable<IngredientListItem> {
    return this.http.post<IngredientListItem>(
      `${this.getEndpoint()}/api/v1/ingredients`,
      data,
    );
  }

  updateIngredient(
    blsCode: string,
    data: IngredientCreateUpdate,
  ): Observable<IngredientListItem> {
    return this.http.put<IngredientListItem>(
      `${this.getEndpoint()}/api/v1/ingredients/${encodeURIComponent(blsCode)}`,
      data,
    );
  }

  deleteIngredient(blsCode: string): Observable<void> {
    return this.http.delete<void>(
      `${this.getEndpoint()}/api/v1/ingredients/${encodeURIComponent(blsCode)}`,
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // METADATA CRUD
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * List all metadata with pagination.
   *
   * @param limit Max results per page (default 50)
   * @param offset Pagination offset (default 0)
   */
  listMetadata(
    limit = 50,
    offset = 0,
    search?: string,
    unitsFilter?: string,
    numericFilters?: Record<string, { value: number; matchMode: string }>,
    orderBy?: string,
    orderDir?: 'asc' | 'desc',
    textFilters?: Record<string, TextFilterValue>,
  ): Observable<MetadataListResponse> {
    let params = new HttpParams().set('limit', limit).set('offset', offset);
    if (search) params = params.set('search', search);
    if (textFilters) {
      for (const [field, { value, matchMode }] of Object.entries(textFilters)) {
        params = params.set(`tf__${field}__${matchMode}`, value);
      }
    }
    if (unitsFilter) params = params.set('units_filter', unitsFilter);
    if (numericFilters) {
      for (const [field, { value, matchMode }] of Object.entries(
        numericFilters,
      )) {
        params = params.set(`nf__${field}__${matchMode}`, value);
      }
    }
    if (orderBy) params = params.set('order_by', orderBy);
    if (orderDir) params = params.set('order_dir', orderDir);
    return this.http.get<MetadataListResponse>(
      `${this.getEndpoint()}/api/v1/metadata/list`,
      { params },
    );
  }

  /**
   * Get single metadata by BLS code.
   *
   * @param blsCode BLS ingredient code
   */
  getMetadata(blsCode: string): Observable<MetadataListItem> {
    return this.http.get<MetadataListItem>(
      `${this.getEndpoint()}/api/v1/metadata/${encodeURIComponent(blsCode)}`,
    );
  }

  /**
   * Create new metadata.
   *
   * @param data Metadata to create
   */
  createMetadata(data: MetadataCreateUpdate): Observable<MetadataListItem> {
    return this.http.post<MetadataListItem>(
      `${this.getEndpoint()}/api/v1/metadata`,
      data,
    );
  }

  /**
   * Update existing metadata.
   *
   * @param blsCode BLS ingredient code
   * @param data Updated metadata
   */
  updateMetadata(
    blsCode: string,
    data: MetadataCreateUpdate,
  ): Observable<MetadataListItem> {
    return this.http.put<MetadataListItem>(
      `${this.getEndpoint()}/api/v1/metadata/${encodeURIComponent(blsCode)}`,
      data,
    );
  }

  updateMetadataById(
    metadataId: number,
    data: MetadataCreateUpdate,
  ): Observable<MetadataListItem> {
    return this.http.put<MetadataListItem>(
      `${this.getEndpoint()}/api/v1/metadata/by-id/${metadataId}`,
      data,
    );
  }

  /**
   * Delete metadata.
   *
   * @param blsCode BLS ingredient code
   */
  deleteMetadata(blsCode: string): Observable<void> {
    return this.http.delete<void>(
      `${this.getEndpoint()}/api/v1/metadata/${encodeURIComponent(blsCode)}`,
    );
  }

  deleteMetadataById(metadataId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.getEndpoint()}/api/v1/metadata/by-id/${metadataId}`,
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // UNITS CRUD
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * List all units with pagination.
   *
   * @param limit Max results per page (default 50)
   * @param offset Pagination offset (default 0)
   */
  listUnits(limit = 50, offset = 0): Observable<UnitListResponse> {
    const params = new HttpParams().set('limit', limit).set('offset', offset);
    return this.http.get<UnitListResponse>(
      `${this.getEndpoint()}/api/v1/units/list`,
      { params },
    );
  }

  /**
   * Get single unit by ID.
   *
   * @param unitId Unit ID
   */
  getUnit(unitId: number): Observable<UnitListItem> {
    return this.http.get<UnitListItem>(
      `${this.getEndpoint()}/api/v1/units/${unitId}`,
    );
  }

  /**
   * Create new unit.
   *
   * @param data Unit to create
   */
  createUnit(data: UnitCreateUpdate): Observable<UnitListItem> {
    return this.http.post<UnitListItem>(
      `${this.getEndpoint()}/api/v1/units`,
      data,
    );
  }

  /**
   * Update existing unit.
   *
   * @param unitId Unit ID
   * @param data Updated unit
   */
  updateUnit(unitId: number, data: UnitCreateUpdate): Observable<UnitListItem> {
    return this.http.put<UnitListItem>(
      `${this.getEndpoint()}/api/v1/units/${unitId}`,
      data,
    );
  }

  /**
   * Delete unit.
   *
   * @param unitId Unit ID
   */
  deleteUnit(unitId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.getEndpoint()}/api/v1/units/${unitId}`,
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // BLS STAMMDATEN (read-only source pool)
  // ───────────────────────────────────────────────────────────────────────────

  listBlsStammdaten(
    limit = 50,
    offset = 0,
    search?: string,
    orderBy?: string,
    orderDir?: 'asc' | 'desc',
    textFilters?: Record<string, TextFilterValue>,
  ): Observable<BlsStammdatenListResponse> {
    let params = new HttpParams().set('limit', limit).set('offset', offset);
    if (search) params = params.set('search', search);
    if (textFilters) {
      for (const [field, { value, matchMode }] of Object.entries(textFilters)) {
        params = params.set(`tf__${field}__${matchMode}`, value);
      }
    }
    if (orderBy) params = params.set('order_by', orderBy);
    if (orderDir) params = params.set('order_dir', orderDir);
    return this.http.get<BlsStammdatenListResponse>(
      `${this.getEndpoint()}/api/v1/stammdaten/list`,
      { params },
    );
  }

  createStammdaten(data: StammdatenCreate): Observable<BlsStammdatenListItem> {
    return this.http.post<BlsStammdatenListItem>(
      `${this.getEndpoint()}/api/v1/stammdaten`,
      data,
    );
  }
}
